import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

// Detect if the app is running as an installed PWA (iOS Safari standalone
// or Android Chrome standalone). iOS OAuth redirects break out of the PWA
// WebView back to Safari, orphaning the session — so we surface a
// paste-transfer-code workflow only for PWA users.
function isPwaStandalone() {
  if (typeof window === "undefined") return false;
  const displayModeStandalone = window.matchMedia
    && window.matchMedia("(display-mode: standalone)").matches;
  const iosStandalone = window.navigator && window.navigator.standalone === true;
  return !!(displayModeStandalone || iosStandalone);
}

export default function Login() {
  const [params] = useSearchParams();
  const error = params.get("error");
  const navigate = useNavigate();
  const { refresh } = useAuth();
  // Environment flags — the backend's public root exposes `demo_mode` and
  // `live_trading_enabled`. We use them to hide the demo button on production
  // and show a first-time-setup banner when the operator hasn't finished
  // Google OAuth yet.
  const [env, setEnv] = useState(null);
  const [isPwa] = useState(isPwaStandalone());
  const [transferCode, setTransferCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    api.get("/").then(({ data }) => setEnv(data)).catch(() => setEnv({ demo_mode: false }));
  }, []);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
    // THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl,
    )}`;
  };

  const handleRedeemTransfer = async () => {
    const code = (transferCode || "").trim().toUpperCase();
    if (code.length !== 6) {
      toast.error("Enter the 6-character transfer code");
      return;
    }
    setRedeeming(true);
    try {
      const { data } = await api.post("/auth/pwa-transfer/redeem", { code });
      // session_token now lives ONLY in the httpOnly cookie set by the
      // backend response — no localStorage duplicate.
      void data;
      const u = await refresh();
      if (u) {
        toast.success("PWA session activated");
        navigate("/dashboard", { replace: true, state: { user: u } });
      } else {
        toast.error("Session verification failed");
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) toast.error("Code not found or already used");
      else if (status === 410) toast.error("Code expired — mint a new one in Safari");
      else if (status === 400) toast.error("Invalid transfer code");
      else toast.error("Redeem failed");
    } finally {
      setRedeeming(false);
    }
  };

  const handleDemoLogin = async () => {
    try {
      const { data } = await api.post("/auth/dev-login");
      // session_token lives in the httpOnly cookie — no localStorage.
      void data;
      // Refresh once and only navigate if the user is actually set
      const u = await refresh();
      if (u) {
        navigate("/dashboard", { replace: true, state: { user: u } });
      } else {
        toast.error("Session verification failed");
      }
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        console.error("[demo-login] error", e);
      }
      toast.error("Demo login failed");
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center relative bg-[#0a0f1a]"
      style={{
        backgroundImage:
          "url('https://images.unsplash.com/photo-1714548529197-537c1f0b6aa7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxkYXJrJTIwYWJzdHJhY3QlMjB0ZWNoJTIwZ3JpZHxlbnwwfHx8fDE3ODQ1NjcyODR8MA&ixlib=rb-4.1.0&q=85')",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      data-testid="login-page"
    >
      <div className="absolute inset-0 bg-black/75 pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, rgba(59,130,246,0.08) 0%, transparent 40%)" }} />

      <div className="relative z-10 w-full max-w-md px-8">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.3em] text-blue-400 mb-3">
            <span className="inline-block w-2 h-2 rounded-full bg-blue-500 mr-2 align-middle animate-pulse" />
            Paper Trading Terminal
          </div>
          <h1 className="text-4xl font-light tracking-tight text-white">
            MyEdge<span className="text-blue-400"> Trader</span>
          </h1>
          <p className="mt-2 text-sm text-slate-400 leading-relaxed">
            NIFTY &amp; BANKNIFTY long-options intraday paper-trading &amp; observability desk. Deterministic signals, executable-depth fills, and full cost transparency.
          </p>
        </div>

        <div className="surface rounded-sm p-6" style={{ background: "rgba(19,28,45,0.9)", backdropFilter: "blur(6px)" }}>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-4">Sign in to continue</div>
          {isPwa && (
            <div
              className="mb-4 rounded-sm border border-amber-500/40 bg-amber-500/10 px-3 py-3"
              data-testid="pwa-oauth-banner"
            >
              <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 mb-1">
                iOS PWA sign-in
              </div>
              <p className="text-xs text-slate-200 leading-relaxed">
                iOS opens Google sign-in in Safari, not the installed app. Sign in once in Safari,
                then tap <span className="font-mono text-amber-300">"Transfer to PWA"</span> on the
                Dashboard to get a 6-character code. Paste it below to activate this PWA session.
              </p>
            </div>
          )}
          <button
            data-testid="google-login-btn"
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-sm border border-slate-700 hover:border-blue-500 hover:bg-slate-800/50 transition-colors text-slate-100"
          >
            <svg width="18" height="18" viewBox="0 0 48 48">
              <path
                fill="#FFC107"
                d="M43.6 20.5H42V20.4H24v7.2h11.3c-1.5 4-5.3 6.9-11.3 6.9-6.8 0-12.3-5.5-12.3-12.3S17.2 9.9 24 9.9c3.1 0 5.9 1.2 8.1 3.1l5.1-5.1C33.9 4.7 29.2 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21 20.4-8.4 20.4-21c0-1.2-.1-2.3-.4-3.5z"
              />
              <path
                fill="#FF3D00"
                d="M6.3 14.7l5.9 4.3C13.8 15.3 18.5 12 24 12c3.1 0 5.9 1.2 8.1 3.1l5.1-5.1C33.9 6.7 29.2 5 24 5c-7.4 0-13.7 4.1-17.7 9.7z"
              />
              <path
                fill="#4CAF50"
                d="M24 45c5.1 0 9.7-1.9 13.2-5l-6.1-5.2c-2 1.4-4.5 2.2-7.1 2.2-6 0-9.8-3-11.3-7l-6 4.6C10.1 40.6 16.5 45 24 45z"
              />
              <path
                fill="#1976D2"
                d="M43.6 20.5H42V20.4H24v7.2h11.3c-.7 1.9-1.9 3.6-3.5 4.9l6.1 5.2C40.9 34.8 44 29.8 44 24c0-1.2-.1-2.3-.4-3.5z"
              />
            </svg>
            <span className="text-sm tracking-wide">Continue with Google</span>
          </button>
          {isPwa && (
            <div className="mt-5 pt-4 border-t border-slate-800" data-testid="pwa-transfer-section">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">
                Paste PWA transfer code
              </div>
              <div className="flex gap-2">
                <input
                  data-testid="pwa-transfer-code-input"
                  value={transferCode}
                  onChange={(e) => setTransferCode(e.target.value.toUpperCase())}
                  placeholder="6-char code"
                  maxLength={6}
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  className="flex-1 bg-slate-900 border border-slate-700 rounded-sm px-3 py-2 text-sm font-mono text-slate-100 tracking-[0.3em] uppercase focus:border-blue-500 outline-none"
                />
                <button
                  data-testid="pwa-transfer-redeem-btn"
                  onClick={handleRedeemTransfer}
                  disabled={redeeming || transferCode.trim().length !== 6}
                  className="px-4 py-2 rounded-sm bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs uppercase tracking-[0.15em]"
                >
                  {redeeming ? "…" : "Activate"}
                </button>
              </div>
              <div className="mt-2 text-[10px] text-slate-500 leading-relaxed">
                Codes expire in 5 minutes. Mint one from the Dashboard in Safari after signing in.
              </div>
            </div>
          )}
          {error && params.get("reason") === "access_denied" && (
            <div
              className="mt-4 rounded-sm border border-amber-500/40 bg-amber-500/10 px-4 py-4"
              data-testid="access-denied-banner"
            >
              <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 mb-2">
                Access restricted
              </div>
              <div className="text-sm text-amber-100 leading-relaxed">
                This workspace is private. Your Google account is not on the
                owner allowlist, so no session was created and no data is
                accessible. Contact the workspace owner if you believe you
                should have access.
              </div>
            </div>
          )}
          {error && params.get("reason") !== "access_denied" && (
            <div className="mt-4 text-xs text-red-400" data-testid="login-error">
              <div>Authentication failed. Please try again.</div>
              {(params.get("reason") || params.get("detail")) && (
                <div className="mt-1 font-mono text-[10px] text-slate-500 break-all">
                  {params.get("reason") || error}
                  {params.get("detail") ? ` · ${params.get("detail")}` : ""}
                </div>
              )}
            </div>
          )}
          {env && env.demo_mode === false && (
            <div
              className="mt-4 rounded-sm border border-blue-500/30 bg-blue-500/10 px-3 py-3"
              data-testid="production-setup-banner"
            >
              <div className="text-[10px] uppercase tracking-[0.2em] text-blue-300 mb-1">
                First-time setup
              </div>
              <p className="text-xs text-slate-300 leading-relaxed">
                Sign in with your Google account above. After landing on the Dashboard, tap
                <span className="font-mono text-blue-300"> Connect Zerodha</span> to link your
                Kite account, then optionally save your 2FA seed so future daily logins take
                ~5 seconds via the Telegram morning nudge.
              </p>
            </div>
          )}
          {env && env.demo_mode && (
            <div className="mt-4 pt-4 border-t border-slate-800">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Or explore instantly</div>
              <button
                data-testid="demo-login-btn"
                onClick={handleDemoLogin}
                className="w-full py-2.5 px-4 rounded-sm border border-amber-500/40 hover:border-amber-500 hover:bg-amber-500/10 transition-colors text-amber-300 text-sm tracking-wide"
              >
                Enter Demo Mode
              </button>
              <div className="mt-2 text-[10px] text-slate-500 leading-relaxed">
                Preloaded synthetic session. No Google account required.
              </div>
            </div>
          )}
          <div className="mt-6 pt-4 border-t border-slate-800 text-[10px] uppercase tracking-[0.15em] text-slate-500">
            Single-user, encrypted session. All actions audit-logged.
          </div>
        </div>

        <p className="mt-6 text-[10px] uppercase tracking-[0.2em] text-slate-600 leading-relaxed">
          Trading involves risk. Paper results do not guarantee live results. Live order placement is not enabled in this build.
        </p>
        <div className="mt-4 flex gap-4 text-[10px] uppercase tracking-[0.15em] text-slate-600" data-testid="legal-links">
          <a href="/privacy" className="hover:text-blue-400">Privacy</a>
          <span className="text-slate-800">·</span>
          <a href="/terms" className="hover:text-blue-400">Terms</a>
        </div>
      </div>
    </div>
  );
}
